/**
 * Simulated GET /api/m365/calendar — My Day → Calendar.
 *
 * Shape is what `getTeamCalendar` in src/lib/api/calendar.ts reads:
 * `{ events, mailboxes, view }` with no `ok:false`, which the client turns into
 * `configured: true`.
 *
 * Derived from the snapshot
 *   • Recurring meetings keep their real titles from the `meetings` table and
 *     their real facilitator from `profiles`.
 *   • Job events name a real job number, a real customer and a real loss city
 *     from `v_jobs_full`, and the `work_day_assignments` rows that fall inside
 *     the window become scheduled crew visits.
 *   • Organizers, their e-mail addresses and team membership are real
 *     (`profiles`, `team_members`).
 *
 * Generated: which weekday slot each event lands in — one deterministic draw
 * per (organizer, date), so the week is stable across reloads.
 */
import { loadTable } from '~/demo/data';
import { seededFloat, seededInt } from './prng';
import { NY_TZ, nyDate, str } from './finance';
import { DEMO_USER_ID } from '~/demo/client/auth';

interface Person { user_id: string; name: string; email: string }

const SLOTS = [8, 9, 10, 11, 13, 14, 15, 16];

function at(date: string, hour: number, minutes = 0): string {
  return `${date}T${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

export async function simulatedCalendar(team: string, range?: { start?: string; end?: string }) {
  const [profiles, teamMembers, meetings, jobs, assignments] = await Promise.all([
    loadTable('profiles'), loadTable('team_members'), loadTable('meetings'),
    loadTable('v_jobs_full'), loadTable('work_day_assignments'),
  ]);

  const people: Person[] = profiles
    .filter((p) => p.is_active !== false && str(p.display_name))
    .map((p) => ({ user_id: str(p.user_id), name: str(p.display_name), email: str(p.email) }));
  const byId = new Map(people.map((p) => [p.user_id, p]));

  const mine = team === 'mine' || !team;
  const me = byId.get(DEMO_USER_ID) ?? people[0];
  const memberIds = new Set(
    teamMembers.filter((m) => str(m.team_id) === team).map((m) => str(m.user_id)),
  );
  const organizers: Person[] = mine
    ? (me ? [me] : [])
    : people.filter((p) => memberIds.has(p.user_id)).slice(0, 8);
  const cast = organizers.length ? organizers : people.slice(0, 4);

  // 14-day window, matching the production default.
  const startDate = range?.start ? range.start.slice(0, 10) : nyDate();
  const days: string[] = [];
  const cursor = new Date(`${startDate}T12:00:00`);
  const endDate = range?.end ? range.end.slice(0, 10) : null;
  for (let i = 0; i < 14; i++) {
    const d = nyDate(cursor);
    if (endDate && d > endDate) break;
    days.push(d);
    cursor.setDate(cursor.getDate() + 1);
  }

  // Real, live jobs give the events their subject matter.
  const liveJobs = jobs
    .filter((j) => str(j.status_bucket) !== 'closed' && str(j.job_number) && str(j.customer_name))
    .slice(0, 300);
  const meetingTitles = [...new Set(meetings.map((m) => str(m.title)).filter(Boolean))];
  const assignedJobIds = new Set(assignments.map((a) => str(a.job_id)));
  const scheduledJobs = liveJobs.filter((j) => assignedJobIds.has(str(j.id)));

  const events: Record<string, unknown>[] = [];
  let seq = 0;

  for (const date of days) {
    const dow = new Date(`${date}T12:00:00`).getDay();
    if (dow === 0 || dow === 6) continue;

    // Monday: the recurring leadership meeting, under its real title.
    if (dow === 1 && meetingTitles.length) {
      const host = cast[seededInt(`${date}|host`, 0, cast.length - 1)];
      events.push({
        id: `demo-cal-${seq++}`,
        subject: meetingTitles[0],
        start: at(date, 9), startTimeZone: NY_TZ,
        end: at(date, 10, 30), endTimeZone: NY_TZ,
        organizerName: host.name, organizerEmail: host.email,
        attendeeCount: Math.min(cast.length + 3, 12),
        categories: ['Meeting'], webLink: null, isCall: false, isOnCall: false,
      });
    }

    // Two or three job-shaped events a day per organizer in view.
    for (const person of cast.slice(0, mine ? 1 : 4)) {
      const count = seededInt(`${person.user_id}|${date}|n`, 1, 3);
      for (let i = 0; i < count; i++) {
        const job = scheduledJobs.length && i === 0
          ? scheduledJobs[seededInt(`${person.user_id}|${date}|sched`, 0, scheduledJobs.length - 1)]
          : liveJobs[seededInt(`${person.user_id}|${date}|${i}|job`, 0, Math.max(0, liveJobs.length - 1))];
        if (!job) continue;
        const hour = SLOTS[seededInt(`${person.user_id}|${date}|${i}|slot`, 0, SLOTS.length - 1)];
        const isCall = seededFloat(`${person.user_id}|${date}|${i}|call`, 0, 1) < 0.35;
        const kind = isCall ? 'Adjuster call' : i === 0 ? 'Site visit' : 'Walkthrough';
        events.push({
          id: `demo-cal-${seq++}`,
          subject: `${kind} — ${str(job.job_number)} · ${str(job.customer_name)}`,
          start: at(date, hour, isCall ? 30 : 0), startTimeZone: NY_TZ,
          end: at(date, hour + 1, isCall ? 0 : 30), endTimeZone: NY_TZ,
          organizerName: person.name, organizerEmail: person.email,
          attendeeCount: isCall ? 2 : seededInt(`${person.user_id}|${date}|${i}|att`, 2, 5),
          categories: [isCall ? 'Call' : 'Job'],
          webLink: null,
          isCall,
          isOnCall: false,
          location: str(job.loss_city) ? `${str(job.loss_city)}, ${str(job.loss_state)}` : null,
        });
      }
    }

    // One ON-CALL duty block a week, rotating through the cast.
    if (dow === 1) {
      const onCall = cast[seededInt(`${date}|oncall`, 0, cast.length - 1)];
      events.push({
        id: `demo-cal-${seq++}`,
        subject: `ON-CALL — ${onCall.name}`,
        start: at(date, 17), startTimeZone: NY_TZ,
        end: at(days[Math.min(days.length - 1, days.indexOf(date) + 6)] ?? date, 8), endTimeZone: NY_TZ,
        organizerName: onCall.name, organizerEmail: onCall.email,
        attendeeCount: 1, categories: ['On-Call'], webLink: null,
        isCall: false, isOnCall: true,
      });
    }
  }

  events.sort((a, b) => str(a.start).localeCompare(str(b.start)));

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Calendar events are generated onto real weekdays from real people (profiles/team_members), real recurring '
      + 'meeting titles (meetings) and real jobs (v_jobs_full, with work_day_assignments taking the first slot).',
    configured: true,
    events,
    mailboxes: mine ? 1 : cast.length,
    view: mine ? 'mine' : 'team',
  };
}
